/**
 * Membership API client — maps the public endpoints (contracts/api.md) to discriminated-union results
 * the pages render. Mirrors lib/health.ts: relative /api URLs, an AbortController client timeout, and
 * explicit handling of ok / 400 / 429 / network error. No throwing — every outcome is a typed result.
 */
const CLIENT_TIMEOUT_MS = 10_000;

export interface ApplyPayload {
  fullName: string;
  email: string;
  message?: string;
  howHeard?: string;
  valuesAffirmed: boolean;
  dataConsent: boolean;
  nonce: string;
  /** Honeypot — always sent empty by real users; bots fill it. */
  website?: string;
}

export type ApplyResult =
  | { kind: 'ok'; emailQueued: boolean }
  | { kind: 'invalid'; errors: Record<string, string> }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

export type WithdrawResult =
  | { kind: 'withdrawn' }
  | { kind: 'invalid' }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

function withTimeout(signal?: AbortSignal): { init: RequestInit; done: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });
  return {
    init: { signal: controller.signal, headers: { accept: 'application/json' } },
    done: () => clearTimeout(timer),
  };
}

/** Fetch the anti-bot timing nonce for a form render. Returns null if unreachable. */
export async function getApplyToken(signal?: AbortSignal): Promise<string | null> {
  const { init, done } = withTimeout(signal);
  try {
    const res = await fetch('/api/membership/apply-token', init);
    if (!res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { token?: string };
    return body.token ?? null;
  } catch {
    return null;
  } finally {
    done();
  }
}

export async function submitApplication(payload: ApplyPayload, signal?: AbortSignal): Promise<ApplyResult> {
  const { init, done } = withTimeout(signal);
  try {
    const res = await fetch('/api/membership/apply', {
      ...init,
      method: 'POST',
      headers: { ...init.headers, 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const body = (await res.json().catch(() => ({}))) as { emailQueued?: boolean };
      return { kind: 'ok', emailQueued: body.emailQueued ?? false };
    }
    if (res.status === 429) return { kind: 'rate_limited' };
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { errors?: Record<string, string> };
      return { kind: 'invalid', errors: body.errors ?? {} };
    }
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  } finally {
    done();
  }
}

export async function withdrawApplication(token: string, signal?: AbortSignal): Promise<WithdrawResult> {
  const { init, done } = withTimeout(signal);
  try {
    const res = await fetch('/api/membership/withdraw', {
      ...init,
      method: 'POST',
      headers: { ...init.headers, 'content-type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (res.status === 429) return { kind: 'rate_limited' };
    if (!res.ok) return { kind: 'error' };
    const body = (await res.json().catch(() => ({}))) as { withdrawn?: boolean };
    return body.withdrawn ? { kind: 'withdrawn' } : { kind: 'invalid' };
  } catch {
    return { kind: 'error' };
  } finally {
    done();
  }
}
